import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuMutationObserver = null;
  #activateTimeout = null;
  #deactivateTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#clearTimers();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#queueDeactivate();
  };

  #state = {
    activeItem: null,
  };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    this.#clearDeactivateTimer();

    const item = findMenuItem(event.target);
    if (!item) return;

    if (item === this.#state.activeItem) return;

    this.#activateTimeout = window.setTimeout(() => {
      this.dispatchEvent(new MegaMenuHoverEvent());

      const isDefaultSlot = item.slot === '';

      this.dataset.overflowExpanded = (!isDefaultSlot).toString();

      const previouslyActiveItem = this.#state.activeItem;
      if (previouslyActiveItem) {
        previouslyActiveItem.ariaExpanded = 'false';
      }

      this.#state.activeItem = item;
      this.ariaExpanded = 'true';
      item.ariaExpanded = 'true';

      let submenu = findSubmenu(item);
      const hasSubmenu = Boolean(submenu);

      if (!hasSubmenu && !isDefaultSlot) {
        submenu = this.overflowMenu;
      }

      if (submenu) {
        submenu.dataset.active = '';
        this.#cleanupMutationObserver();

        this.#submenuMutationObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (submenu.offsetHeight > 0) {
                this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
                this.#cleanupMutationObserver();
              }
            });
          });
        });

        this.#submenuMutationObserver.observe(submenu, {
          childList: true,
          subtree: true,
        });

        window.setTimeout(() => {
          this.#cleanupMutationObserver();
        }, 500);
      }

      let finalHeight = submenu?.offsetHeight || 0;

      if (!isDefaultSlot) {
        const overflowListHeight = this.#getOverflowListLinksHeight();

        if (hasSubmenu) {
          const overflowHeight = this.overflowMenu?.offsetHeight || 0;
          finalHeight = Math.max(overflowHeight, overflowListHeight);
        } else {
          finalHeight = overflowListHeight;
        }
      }

      if (!submenu) {
        finalHeight = 0;
      }

      this.headerComponent?.style.setProperty('--submenu-height', `${finalHeight}px`);
      this.#setFullOpenHeaderHeight(finalHeight);
      this.style.setProperty('--submenu-opacity', '1');
    }, 40);
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu =
      event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#queueDeactivate();
  }

  #queueDeactivate() {
    this.#clearActivateTimer();

    this.#deactivateTimeout = window.setTimeout(() => {
      this.#deactivate();
    }, 120);
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (
          link.querySelector('[ref="submenu[]"]')
        );
        if (submenu) cb(submenu);
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });

    const height = this.overflowMenu?.offsetHeight || 0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });

    return height;
  }

  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (
            this.headerComponent.querySelector('.header__row--top')
          )?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const fullOpenHeaderHeight = submenuHeight === 0 ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  #clearActivateTimer() {
    if (this.#activateTimeout) {
      window.clearTimeout(this.#activateTimeout);
      this.#activateTimeout = null;
    }
  }

  #clearDeactivateTimer() {
    if (this.#deactivateTimeout) {
      window.clearTimeout(this.#deactivateTimeout);
      this.#deactivateTimeout = null;
    }
  }

  #clearTimers() {
    this.#clearActivateTimer();
    this.#clearDeactivateTimer();
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item');
  if (!(listItem instanceof HTMLElement)) return null;

  if (listItem.matches('[slot="more"]')) {
    return /** @type {HTMLElement | null} */ (
      listItem.parentElement?.querySelector('[slot="overflow"] [ref="menuitem"]')
    );
  }

  return /** @type {HTMLElement | null} */ (
    listItem.querySelector('[ref="menuitem"]')
  );
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}